/** AP-28 — Typen der Artikelverwaltung (Vertrag: docs/Arbeitspakete/AP-28/AP-28_Artikelstamm.md §7). */

export type ArticleKind = 'SERVICE' | 'MATERIAL' | 'EQUIPMENT' | 'OTHER'

export const ARTICLE_KINDS: readonly ArticleKind[] = ['MATERIAL', 'SERVICE', 'EQUIPMENT', 'OTHER']

// Die Preiseinheit ist seit dem AP-28-Nachtrag (N-12) eine freie ganze Zahl
// 1–1.000.000 — Grenze, Prüfung und Vorschläge stehen in `@symphenia/shared-types`.

export interface ArticleUnit {
  id: string
  code: string
  name: string
  decimals: number
  isActive: boolean
  sortOrder?: number
  articleCount?: number
}

export interface ArticleGroup {
  id: string
  name: string
  isActive: boolean
  sortOrder?: number
  articleCount?: number
}

export interface SupplierRef {
  id: string
  name: string
  supplierNumber: string | null
  isActive: boolean
}

export interface Article {
  id: string
  articleNumber: string
  name: string
  documentText: string | null
  internalNote: string | null
  kind: ArticleKind
  isSalesArticle: boolean
  isPurchaseArticle: boolean
  unitId: string
  unit: ArticleUnit
  groupId: string | null
  group: ArticleGroup | null
  salesPrice: number | null
  priceUnit: number
  taxRate: number | null
  /** Kilogramm je Artikeleinheit (N-7), vier Nachkommastellen. */
  weightPerUnit: number | null
  /** GTIN-8/12/13/14, eindeutig je Mandant (N-19). */
  ean: string | null
  imagePath: string | null
  hasImage: boolean
  /** Presigned-Link (15 min) — `null` ohne Bild oder bei nicht erreichbarem Speicher */
  imageUrl?: string | null
  isActive: boolean
  externalId: string | null
  externalSource: string | null
  createdAt: string
  updatedAt: string

  // ── Nur mit `articles.purchase.view` — ohne Recht fehlen die Schlüssel ganz ──
  preferredSupplier?: SupplierRef | null
  purchasePrice?: number | null
  purchasePriceUnit?: number | null
  purchaseCurrency?: string | null
  supplierArticleNumber?: string | null
}

export interface ArticleSupplierRow {
  id: string
  supplierId: string
  supplier: SupplierRef
  supplierArticleNumber: string | null
  purchasePrice: number | null
  priceUnit: number
  currency: string | null
  leadTimeDays: number | null
  packSize: number | null
  isPreferred: boolean
  note: string | null
}

/** Alternativeinheit (N-5): `quantity` Alternativeinheit = `baseQuantity` Artikeleinheit. */
export interface ArticleAltUnit {
  id: string
  unitId: string
  unit: { id: string; code: string; name: string }
  quantity: number
  baseQuantity: number
}

export interface AltUnitPayload {
  unitId: string
  quantity: number
  baseQuantity: number
}

export const MAX_ALT_UNITS = 10

/** Kundenpreis (N-8): ein Kunde, ein Preis, eine Preiseinheit. */
export interface CustomerPrice {
  id: string
  customerId: string
  customer: { id: string; name: string; clientNumber: string | null; isActive: boolean }
  price: number
  priceUnit: number
  note: string | null
  /** `JJJJ-MM-TT` — erster gültiger Tag (N-18). */
  validFrom: string
  /** `JJJJ-MM-TT` — letzter gültiger Tag; `null` = ohne Ablaufdatum. */
  validTo: string | null
  createdAt: string
  updatedAt: string
}

export interface CustomerPricePayload {
  customerId?: string
  price?: number
  priceUnit?: number
  note?: string | null
  validFrom?: string
  validTo?: string | null
}

export interface CustomerRef {
  id: string
  name: string
  clientNumber: string | null
  isActive: boolean
}

/** `suppliers` fehlt ohne Feldrecht — nicht leer, gar nicht da. */
export interface ArticleDetail extends Article {
  suppliers?: ArticleSupplierRow[]
  altUnits: ArticleAltUnit[]
  customerPrices: CustomerPrice[]
}

/** Antwort des Bild-Uploads: das Detail plus vorher/nachher, wenn verkleinert wurde (N-13). */
export interface ArticleImageUploadResult extends ArticleDetail {
  imageCompression: { originalBytes: number; storedBytes: number } | null
}

/** Nummernaufbau und Bildschwelle (N-1 bis N-4, N-14). */
export interface ArticleSettings {
  numberPrefix: string
  numberSeparator: string
  numberStart: number
  numberDigits: number
  imageCompressAboveBytes: number
  /** Die tatsächlich nächste Nummer — berechnet vom Server. */
  nextNumber: string
}

export type ArticleSettingsPayload = Partial<Omit<ArticleSettings, 'nextNumber'>>

export const NUMBER_SEPARATORS = ['', '-', '/', '.', '_'] as const

/** Ein möglicher Doppelgänger, wie ihn die Anlageseite zeigt (N-21). */
export interface ArticleDuplicate {
  id: string
  articleNumber: string
  name: string
  ean: string | null
  isActive: boolean
  matchedOn: ('name' | 'ean')[]
}

export interface ListArticlesParams {
  search?: string
  /** AP-40: ohne Preis beim Vorzugslieferanten · Preis alt · ohne Lieferant */
  purchaseState?: 'NO_PRICE' | 'OLD_PRICE' | 'NO_SUPPLIER'
  priceBefore?: string
  changedFrom?: string
  /** Volltextsuche (N-20): jedes Wort muss irgendwo vorkommen. */
  fulltext?: string
  kind?: ArticleKind
  groupId?: string
  role?: 'sales' | 'purchase'
  supplierId?: string
  isActive?: 'true' | 'false' | 'all'
  priceFrom?: number
  priceTo?: number
  sortBy?: string
  sortDir?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export interface Paginated<T> {
  data: T[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}

/** Abschnittsweise: Jede Maske schickt nur ihre eigenen Felder (AP-28 6.3). */
export interface ArticlePayload {
  articleNumber?: string
  name?: string
  documentText?: string | null
  internalNote?: string | null
  kind?: ArticleKind
  isSalesArticle?: boolean
  isPurchaseArticle?: boolean
  unitId?: string
  groupId?: string | null
  salesPrice?: number | null
  priceUnit?: number
  taxRate?: number | null
  weightPerUnit?: number | null
  ean?: string | null
}

export interface SupplierRowPayload {
  supplierId?: string
  supplierArticleNumber?: string | null
  purchasePrice?: number | null
  priceUnit?: number
  currency?: string | null
  leadTimeDays?: number | null
  packSize?: number | null
  isPreferred?: boolean
  note?: string | null
}
